/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Accordion, Label, Select } from '@iwoca/orion';
import classnames from 'classnames';
import produce from 'immer';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl
} from 'react-intl';
import { connect } from 'react-redux';

import { usePutStateMutation } from '@lendingAPI/lendingAPIMutation';
import { useGetStateQuery } from '@lendingAPI/lendingAPIQueries';
import { RootState } from '@src/reducers';
import { getAmplitudeDefaultEventProperties } from '@src/tracking/amplitudeEvents';
import { getSplitTestValue } from '@src/utils/selectors';

import messages from './BusinessBankStatementManual.messages';
import { getCountryCode } from '../../../../lendingAPI/lendingAPISelector';
import FileInput from '../../FileInput/FileInput';
import UploadedFilesStatus from '../../FileUpload/UploadedFilesStatus';
import { documentTypes } from '../../TradingHistory.helpers';
import styles from '../BusinessBankStatement.module.css';

const mapStateToProps = (state: RootState) => {
    return {
        isOpenBankingPreference: getSplitTestValue(
            state,
            'open_banking_preference'
        ),
        country: getCountryCode(state)
    };
};

const BusinessBankStatementManual = ({
    isOpenBankingPreference,
    isOptional,
    intl,
    country,
    isInLinkOBBranch
}: {
    isOpenBankingPreference: boolean;
    isOptional: boolean;
    country: 'uk' | 'de';
    isInLinkOBBranch?: boolean;
} & WrappedComponentProps) => {
    const { mutatePutState } = usePutStateMutation();

    const { state } = useGetStateQuery();

    const [showManualUploadButton, setShowManualUploadButton] = useState(
        country === 'uk'
    );

    const manualUploadReasonsDE = [
        {
            value: 1,
            text: 'Ich habe kein Online Banking.'
        },
        {
            value: 2,
            text: 'Ich finde PDF hochladen einfacher.'
        },
        {
            value: 3,
            text: 'Ich konnte meine Bank nicht finden.'
        },
        {
            value: 4,
            text: 'Ich vertraue der Open Banking Lösung nicht.'
        },
        {
            value: 5,
            text: 'Ich finde, dass ich mit Open Banking mehr Informationen teile als notwendig.'
        },
        {
            value: 6,
            text: 'Ich möchte die Frage nicht beantworten.'
        }
    ];

    const submitManualReasonDE = async (manualReasonData: string) => {
        const updatedStateData = produce(state, (newState) => {
            newState.ui = {
                ...newState.ui,
                OB_not_linked_reason: manualReasonData
            };
        });
        await mutatePutState({
            body: { data: updatedStateData }
        });
    };

    const renderManualReasonSelectDE = () => {
        return (
            <div className={styles.manualReasonsContainer}>
                <Label htmlFor="manual-bank-statement-reason">
                    <FormattedMessage {...messages.selectManualReasonText} />
                </Label>
                <Select
                    defaultValue="none"
                    name="manual-bank-statement-reason"
                    className={styles.manualSelectInput}
                    onChange={(e) => {
                        if (e.target.value !== 'none') {
                            setShowManualUploadButton(true);
                        }
                    }}
                    onBlur={(e) => submitManualReasonDE(e.target.value)}>
                    <optgroup label="Ich kann mein Bankkonto nicht verknüpfen">
                        <option disabled hidden value="none">
                            Bitte wählen Sie eine Option aus
                        </option>
                        {manualUploadReasonsDE.map((manualreason) => (
                            <option
                                key={manualreason.value}
                                value={manualreason.text}>
                                {manualreason.text}
                            </option>
                        ))}
                    </optgroup>
                </Select>
            </div>
        );
    };

    const renderDescriptionList = () => {
        return (
            <>
                <ul>
                    <li data-testid="how-to-doc">
                        <FormattedMessage
                            id={
                                isOpenBankingPreference && country === 'uk'
                                    ? `${messages.howToDownloadBankStatement.id}`
                                    : `${messages.point1.id}`
                            }
                            defaultMessage={
                                isOpenBankingPreference && country === 'uk'
                                    ? `${messages.howToDownloadBankStatement.defaultMessage}`
                                    : `${messages.point1.defaultMessage}`
                            }
                            values={{
                                a: (chunks) => (
                                    <a
                                        href="https://static.iwoca.com/pdf/iwoca_bank_statement_pdf_download_guide.pdf"
                                        target="_blank"
                                        rel="noreferrer">
                                        {chunks}
                                    </a>
                                )
                            }}
                        />
                    </li>
                    <li>
                        <FormattedMessage
                            {...messages.point2}
                            values={{
                                strong: (chunks) => <strong>{chunks}</strong>
                            }}
                        />
                    </li>
                    <li>
                        <FormattedMessage {...messages.point3} />
                    </li>
                    <li>
                        <FormattedMessage
                            {...messages.point4}
                            values={{
                                a: (chunks) => (
                                    <a
                                        href="" // decide on link
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {chunks}
                                    </a>
                                )
                            }}
                        />
                    </li>
                    {country === 'de' && (
                        <>
                            <li>
                                <FormattedMessage {...messages.point5} />
                            </li>
                            <li>
                                <FormattedMessage {...messages.point6} />
                            </li>
                        </>
                    )}
                </ul>
            </>
        );
    };

    const handleManualAccordion = () => {
        tracking.ampli.accordionClicked({
            accordionName: 'Manual bank statement',
            ...getAmplitudeDefaultEventProperties()
        });
    };

    return (
        <>
            <div
                className={classnames(styles.linkAccountInfoSection, {
                    [styles.accordionManualUploadContainer]:
                        isOpenBankingPreference && country === 'uk'
                })}
                data-testid="manual-uploads">
                {(country === 'uk' && !isOpenBankingPreference) ||
                (country === 'de' && !isInLinkOBBranch) ? (
                    <>
                        <div className={styles.descriptionContainer}>
                            <h3>
                                <FormattedMessage {...messages.title} />
                            </h3>
                            {renderDescriptionList()}
                        </div>
                        <FileInput
                            documentType={documentTypes.bank_statement}
                            buttonText={intl.formatMessage(messages.cta)}
                            isOptional={isOptional}
                        />
                    </>
                ) : (
                    <>
                        <Accordion
                            title={intl.formatMessage(
                                messages.cannotLinkAccount
                            )}
                            small
                            variant="secondary"
                            onClick={handleManualAccordion}>
                            {renderDescriptionList()}
                            <>
                                {country === 'de' &&
                                    renderManualReasonSelectDE()}
                                {(country === 'uk' ||
                                    showManualUploadButton) && (
                                    <FileInput
                                        size="small"
                                        documentType={
                                            documentTypes.bank_statement
                                        }
                                        buttonText={intl.formatMessage(
                                            messages.cta
                                        )}
                                        isOptional={true}
                                    />
                                )}
                            </>
                        </Accordion>
                    </>
                )}
            </div>
            <UploadedFilesStatus documentType={documentTypes.bank_statement} />
        </>
    );
};

export default connect(mapStateToProps)(
    injectIntl(BusinessBankStatementManual)
);
